import { Controller } from "@hotwired/stimulus"

// Attached to each rendered section in admin_edit mode. The header strip
// (drag handle, label, state select, Edit / Type / Delete) is server-rendered
// by app/views/admin/inner_body/_block_chrome.slim — this controller just
// wires the buttons' actions. No DOM injection: server-rendered chrome
// survives Turbo (idiomorph) refreshes naturally, so the previous
// turbo:morph-element re-attach workaround is gone.
//
// Wrapper carries data-controller="admin-block" plus:
//   data-slide-id, data-section-type, data-section-label, data-palette,
//   data-aasm-state, data-admin-block-edit-url-value, data-admin-block-destroy-url-value
// (all set by InnerBodyAdminChromeHelper#inner_body_admin_block_attrs).
export default class extends Controller {
  static targets = ["body", "stateSelect"]
  static values = { editUrl: String, destroyUrl: String }

  connect() {
    if (!this.hasStateSelectTarget) return
    const initial = this.element.dataset.aasmState || "draft"
    this.stateSelectTarget.value = initial
    this._restyleState(this.stateSelectTarget, initial)
  }

  async changeState(event) {
    const select = event.currentTarget
    const newState = select.value
    const previousState = this.element.dataset.aasmState || "draft"
    const csrf = document.querySelector("meta[name=csrf-token]")?.content
    if (!csrf) return
    const body = new FormData()
    body.append("slide[aasm_state]", newState)
    const res = await fetch(this.destroyUrlValue, {
      method: "PATCH",
      headers: { "X-CSRF-Token": csrf, Accept: "text/vnd.turbo-stream.html" },
      body
    })
    if (!res.ok) {
      alert("Couldn't change the section's publication state.")
      select.value = previousState
      return
    }
    this.element.dataset.aasmState = newState
    this._restyleState(select, newState)
  }

  // Mirrors application_helper.rb#aasm_state_classes color buckets so
  // the header's state select reads at-a-glance: gray for draft, green for
  // published, amber for unpublished.
  _restyleState(select, state) {
    select.classList.remove("sh-state-draft", "sh-state-published", "sh-state-unpublished")
    if (state === "published") select.classList.add("sh-state-published")
    else if (state === "unpublished") select.classList.add("sh-state-unpublished")
    else select.classList.add("sh-state-draft")
  }

  openEditor() {
    if (window.Turbo && document.querySelector("turbo-frame#slide-over")) {
      window.Turbo.visit(this.editUrlValue, { frame: "slide-over" })
    } else {
      window.location.href = this.editUrlValue
    }
  }

  openTypePalette() {
    // Toggle: a second click on Type closes the popover instead of stacking.
    const existing = this.element.querySelector(":scope > [data-controller='admin-section-palette']")
    if (existing) { existing.remove(); return }
    const slideId = this.element.dataset.slideId
    const palette = this.element.dataset.palette
    const current = this.element.dataset.sectionType
    // PATCH and DELETE for a slide share the same path (/a/slides/:id) —
    // they differ only by HTTP method. Reuse destroyUrlValue.
    const patchUrl = this.destroyUrlValue
    const popover = document.createElement("div")
    popover.setAttribute("data-controller", "admin-section-palette")
    popover.setAttribute("data-admin-section-palette-mode-value", "change-type")
    popover.setAttribute("data-admin-section-palette-slide-id-value", slideId)
    popover.setAttribute("data-admin-section-palette-palette-value", palette)
    popover.setAttribute("data-admin-section-palette-current-type-value", current)
    popover.setAttribute("data-admin-section-palette-patch-url-value", patchUrl)
    this.element.appendChild(popover)
  }

  // Toggle the figure-typed slide's `force_grid_break_before` flag and
  // hard-reload so the coalescer re-runs and the grid splits (or rejoins)
  // visually. Only rendered in chrome for section_type=="figure" slides.
  async toggleRowBreak(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const csrf = document.querySelector("meta[name=csrf-token]")?.content
    if (!csrf) return
    const current = btn.getAttribute("aria-pressed") === "true"
    const next = !current
    const body = new FormData()
    body.append("slide[force_grid_break_before]", next ? "1" : "0")
    const res = await fetch(this.destroyUrlValue, {
      method: "PATCH",
      headers: { "X-CSRF-Token": csrf, Accept: "text/vnd.turbo-stream.html" },
      body
    })
    if (!res.ok) {
      alert("Couldn't toggle the row break.")
      return
    }
    if (window.Turbo) {
      window.Turbo.cache.clear()
      window.Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }

  async delete() {
    if (!confirm("Delete this section?")) return
    const csrf = document.querySelector("meta[name=csrf-token]")?.content
    if (!csrf) return
    const res = await fetch(this.destroyUrlValue, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrf, Accept: "text/vnd.turbo-stream.html" }
    })
    if (!res.ok) {
      alert("Couldn't delete that section.")
      return
    }
    const body = await res.text()
    if (window.Turbo && body.includes("<turbo-stream")) {
      window.Turbo.renderStreamMessage(body)
    } else {
      this.element.remove()
    }
  }
}
